import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const UploadFormsModal34 = () => {
  const {
    showUploadFormsModal,
    setShowUploadFormsModal,
    uploadFormsProject,
    showNotification,
    setProjects
  } = useAppContext();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  if (!showUploadFormsModal || !uploadFormsProject) return null;
  
  // Only handle Stage 3 & 4 projects
  if (uploadFormsProject.stage < 3) return null;

  // Determine upload type based on project status
  const isSignedQuoteUpload = uploadFormsProject.status === 'stage-3-approved' || 
                              uploadFormsProject.status === 'stage-4-approved';

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showNotification("No Files", "Please select files to upload", "warning");
      return;
    }

    setIsUploading(true);

    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      // Create uploaded forms data
      const uploadedForms = selectedFiles.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadDate: new Date().toISOString()
      }));

      // Different messages based on upload type
      let clientMessage, architectMessage;
      
      if (isSignedQuoteUpload) {
        // Message for signed quote upload
        clientMessage = `Uploaded signed quote approval`;
        if (selectedFiles.length > 1) {
          clientMessage += ` and ${selectedFiles.length - 1} supporting document(s)`;
        }
        clientMessage += '.';
        
        architectMessage = "Thank you for uploading the signed quote approval. Your project will begin once the initial payment has been received by planAIdesign.";
      } else {
        // Message for other documents (consultant agreements, authority forms, etc.)
        clientMessage = `Uploaded ${selectedFiles.length} document(s) for processing.`;
        
        // Check if these might be consultant agreements
        const hasConsultantAgreement = selectedFiles.some(f => 
          f.name.toLowerCase().includes('consultant') || 
          f.name.toLowerCase().includes('agreement')
        );
        
        if (hasConsultantAgreement) {
          architectMessage = "Thank you for uploading the signed consultant agreements. We will proceed with engaging the consultants for your project.";
        } else {
          architectMessage = "Thank you for uploading the documents. We will review and process them accordingly.";
        }
      }

      // Update project with uploaded forms and messages
      const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
      
      const updatedProject = {
        ...uploadFormsProject,
        uploadedForms: [...(uploadFormsProject.uploadedForms || []), ...uploadedForms],
        communications: [
          ...(uploadFormsProject.communications || []),
          {
            type: 'client',
            sender: uploadFormsProject.client,
            message: clientMessage,
            timestamp: timestamp
          },
          {
            type: 'architect',
            sender: 'planAIdesign',
            message: architectMessage,
            timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
          }
        ]
      };

      // Save to Firebase
      await setDoc(doc(db, 'projects', updatedProject.id), updatedProject);

      // Update local state
      setProjects(prev => prev.map(project => 
        project.id === updatedProject.id ? updatedProject : project
      ));

      setIsUploading(false);
      showNotification(
        "Upload Complete", 
        isSignedQuoteUpload ? "Signed quote approval uploaded successfully" : "Documents uploaded successfully",
        "success"
      );
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      showNotification(
        "Upload Error",
        "There was an error uploading your documents. Please try again.",
        "error"
      );
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setShowUploadFormsModal(false);
  };

  // Get appropriate title and instructions
  const getModalContent = () => {
    if (isSignedQuoteUpload) {
      return {
        title: "Upload Signed Quote Approval",
        instructions: "Please upload the signed quote approval document and any other required forms.",
        acceptedFormats: ".pdf,.doc,.docx"
      };
    }
    
    // For other Stage 3/4 uploads
    if (uploadFormsProject.stage === 3) {
      return {
        title: "Upload Documents",
        instructions: "Please upload the required documents for Stage 3 authority approval process.",
        acceptedFormats: ".pdf,.doc,.docx,.jpg,.jpeg,.png"
      };
    } else {
      return {
        title: "Upload Documents",
        instructions: "Please upload the required documents for Stage 4 construction documentation.",
        acceptedFormats: ".pdf,.doc,.docx,.jpg,.jpeg,.png"
      };
    }
  };

  const modalContent = getModalContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">{modalContent.title}</h2>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-3">
            {modalContent.instructions}
          </p>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="w-full"
              accept={modalContent.acceptedFormats}
              disabled={isUploading}
            />
            <p className="text-xs text-gray-500 mt-2">
              Accepted formats: {modalContent.acceptedFormats}
            </p>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold mb-2">Selected Files:</h3>
            <div className="max-h-32 overflow-y-auto">
              <ul className="text-sm text-gray-600">
                {selectedFiles.map((file, idx) => (
                  <li key={idx} className="mb-1 flex justify-between">
                    <span className="truncate flex-1">• {file.name}</span>
                    <span className="text-xs ml-2">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total: {selectedFiles.length} file(s), {(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        )}

        {/* Upload hints for Stage 3/4 */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
          <p className="text-xs text-blue-800">
            <strong>Upload Tips:</strong><br/>
            {isSignedQuoteUpload ? (
              "• Ensure the quote is signed and dated\n• Include any requested additional documents"
            ) : (
              uploadFormsProject.stage === 3 ? 
              "• Include all required authority forms\n• Ensure documents are complete and signed" :
              "• Include all construction documentation\n• Ensure drawings are at correct scale"
            )}
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            disabled={isUploading || selectedFiles.length === 0}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadFormsModal34;